// C:\Projects\texe-web\src\pages\SettingsProfile.tsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiUpload,
  API_BASE_URL,
  changePassword,
  changeUsername,
  deleteMyAccount,
  setToken,
} from "../lib/api";
import { useAuth } from "../context/AuthProvider";
import MoreMenuForUser from "../components/MoreMenuForUser";
import Avatar from "../components/Avatar";
import ImageModal from "../components/ImageModal";
import ImageCropper from "../components/ImageCropper";
import i18n from "../i18n"; // ⬅️ hook yerine direkt i18n instance

type MeResponse = {
  ok: true;
  user: {
    id: string;
    username: string;
    email: string;
    bio?: string | null;
    favoriteQuote?: string | null;
    website?: string | null;
    preferredLanguage?: string | null;
    avatarUrl?: string | null;
    avatarUrlSmall?: string | null;
  };
};

function resolveImg(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  // { code: "de", label: "Deutsch" }, { code: "es", label: "Español" }, ...
];

export default function SettingsProfile() {
  const { user, refreshMe, logout } = useAuth(); // ProtectedRoute zaten login'i zorluyor

  // Collapse states
  const [usernameOpen, setUsernameOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [form, setForm] = useState({
    bio: "",
    favoriteQuote: "",
    website: "",
    preferredLanguage: "tr",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Username form
  const [uname, setUname] = useState("");
  const [unameBusy, setUnameBusy] = useState(false);
  const [unameMsg, setUnameMsg] = useState<string | null>(null);
  const [unameErr, setUnameErr] = useState<string | null>(null);

  // Password form
  const [pwdCur, setPwdCur] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdNew2, setPwdNew2] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState<string | null>(null);
  const [pwdErr, setPwdErr] = useState<string | null>(null);

  // Avatar UI state
  const [avatar, setAvatar] = useState<{ big: string | null; small: string | null }>({
    big: null,
    small: null,
  });
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openAvatar, setOpenAvatar] = useState(false);
  const [cropperSrc, setCropperSrc] = useState<string | null>(null); // Kırpma için
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Debug: cropperSrc değişikliklerini logla
  useEffect(() => {
    console.log("🎨 cropperSrc state changed:", cropperSrc ? "SET (has image)" : "NULL");
  }, [cropperSrc]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setMsg(null);
      setErr(null);
      try {
        const r = await apiGet<MeResponse>("/api/profile/me");
        if (ignore) return;

        setForm({
          bio: r.user.bio ?? "",
          favoriteQuote: r.user.favoriteQuote ?? "",
          website: r.user.website ?? "",
          preferredLanguage: (r.user.preferredLanguage ?? "tr").toLowerCase(),
        });

        setAvatar({
          big: resolveImg(r.user.avatarUrl) ?? null,
          small: resolveImg(r.user.avatarUrlSmall || r.user.avatarUrl) ?? null,
        });

        // Username bölümünü doldur
        setUname(r.user.username);

        // UI dilini kullanıcı seçimine eşitle (varsa)
        if (r.user.preferredLanguage) {
          i18n.changeLanguage(r.user.preferredLanguage);
        }
      } catch (e: any) {
        if (ignore) return;
        setErr(e?.message || "Profil yüklenemedi");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      await apiPatch("/api/profile/me", {
        bio: form.bio || null,
        favoriteQuote: form.favoriteQuote || null,
        website: form.website || null,
        preferredLanguage: form.preferredLanguage || "tr",
      });

      setMsg("Kaydedildi.");
      if (form.preferredLanguage) {
        i18n.changeLanguage(form.preferredLanguage);
      }
      await refreshMe();
    } catch (e: any) {
      setErr(e?.message || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  function chooseFile() {
    fileRef.current?.click();
  }

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const okTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      setErr("Sadece JPEG/PNG/WebP dosyası yükleyebilirsin.");
      e.target.value = "";
      return;
    }
    const max = 5 * 1024 * 1024;
    if (file.size > max) {
      setErr("Dosya boyutu 5 MB’ı aşamaz.");
      e.target.value = "";
      return;
    }

    // Dosyayı cropper'a gönder
    const reader = new FileReader();
    reader.onload = () => {
      console.log("🖼️ FileReader loaded, setting cropperSrc");
      setCropperSrc(reader.result as string);
    };
    reader.onerror = (error) => {
      console.error("❌ FileReader error:", error);
      setErr("Dosya okunamadı. Lütfen tekrar dene.");
    };
    console.log("📂 Starting to read file:", file.name, file.type, file.size);
    reader.readAsDataURL(file);

    if (fileRef.current) fileRef.current.value = "";
  }

  async function onCropComplete(croppedBlob: Blob) {
    setCropperSrc(null);
    setUploading(true);
    setErr(null);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append("avatar", croppedBlob, "avatar.webp");
      const res = await apiUpload<{ ok: true; user: MeResponse["user"] }>(
        "/api/profile/avatar",
        fd
      );

      setAvatar({
        big: resolveImg(res.user.avatarUrl) ?? null,
        small: resolveImg(res.user.avatarUrlSmall || res.user.avatarUrl) ?? null,
      });
      setMsg("Profil fotoğrafın güncellendi.");
      await refreshMe();
    } catch (e: any) {
      setErr(e?.message || "Yükleme başarısız oldu.");
    } finally {
      setUploading(false);
    }
  }

  function onCropCancel() {
    setCropperSrc(null);
  }

  async function onRemoveAvatar() {
    if (!avatar.big && !avatar.small) return;
    if (!confirm("Profil fotoğrafını kaldırmak istiyor musun?")) return;
    setDeleting(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await apiDelete<{ ok: true; user: MeResponse["user"] }>("/api/profile/avatar");
      setAvatar({
        big: resolveImg(res.user.avatarUrl) ?? null,
        small: resolveImg(res.user.avatarUrlSmall || res.user.avatarUrl) ?? null,
      });
      setMsg("Profil fotoğrafın kaldırıldı.");
      await refreshMe();
    } catch (e: any) {
      setErr(e?.message || "Silme işlemi başarısız oldu.");
    } finally {
      setDeleting(false);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Kullanıcı adı değiştir
  // ────────────────────────────────────────────────────────────
  async function onChangeUsername(e: React.FormEvent) {
    e.preventDefault();
    if (!uname.trim() || uname.trim().length < 3) {
      setUnameErr("Kullanıcı adı en az 3 karakter olmalı.");
      return;
    }
    setUnameBusy(true);
    setUnameMsg(null);
    setUnameErr(null);
    try {
      const r = await changeUsername(uname.trim());
      if (r.access) setToken(r.access); // backend yeni access döndürüyor
      setUnameMsg("Kullanıcı adın güncellendi.");
      await refreshMe(); // context & UI senkron
    } catch (e: any) {
      setUnameErr(e?.message || "Güncelleme başarısız.");
    } finally {
      setUnameBusy(false);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Şifre değiştir
  // ────────────────────────────────────────────────────────────
  async function onChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!pwdCur || !pwdNew) {
      setPwdErr("Mevcut ve yeni şifre zorunlu.");
      return;
    }
    if (pwdNew.length < 6) {
      setPwdErr("Yeni şifre en az 6 karakter olmalı.");
      return;
    }
    if (pwdNew !== pwdNew2) {
      setPwdErr("Yeni şifreler uyuşmuyor.");
      return;
    }
    setPwdBusy(true);
    setPwdErr(null);
    setPwdMsg(null);
    try {
      const r = await changePassword(pwdCur, pwdNew);
      if (r.access) setToken(r.access); // token rotation
      setPwdMsg("Şifren güncellendi.");
      setPwdCur("");
      setPwdNew("");
      setPwdNew2("");
    } catch (e: any) {
      setPwdErr(e?.message || "Şifre güncelleme başarısız.");
    } finally {
      setPwdBusy(false);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Hesabı kapat (Soft Delete)
  // ────────────────────────────────────────────────────────────
  async function onSoftDelete() {
    if (!confirm("Hesabını kapatmak istediğine emin misin? Bu işlem geri alınamaz (soft delete).")) return;
    try {
      await deleteMyAccount();
    } catch (e: any) {
      alert(e?.message || "Hesap kapatma başarısız.");
      return;
    }
    // Oturumu kapat ve ana sayfaya dön (router tarafında zaten korumalı sayfalar gizlenecek)
    await logout();
  }

  if (loading) return <div style={{ padding: 24 }}>Yükleniyor...</div>;
  if (!user) return null;

  const hasAvatar = Boolean(avatar.big || avatar.small);

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      {/* Image Cropper Modal */}
      {cropperSrc && (
        <ImageCropper
          imageSrc={cropperSrc}
          onCropComplete={onCropComplete}
          onCancel={onCropCancel}
        />
      )}

      {/* Başlık + 3 nokta menü */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Profil Ayarları</h2>
        <div style={{ marginLeft: "auto" }}>
          <div className="kebab-menu">
            <MoreMenuForUser targetUserId={user.id} includeBlockedList hideBlock />
          </div>
        </div>
      </div>

      {/* Avatar bölümü */}
      <section
        style={{
          border: "1px solid #2c2c2c",
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          display: "grid",
          gridTemplateColumns: "80px 1fr",
          gap: 16,
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Avatar
            src={avatar.small ?? undefined}
            alt={user.username}
            size={72}
            onClick={() => hasAvatar && setOpenAvatar(true)}
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Profil Fotoğrafı</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={chooseFile} disabled={uploading}>
              {uploading ? "Yükleniyor…" : hasAvatar ? "Değiştir" : "Yükle"}
            </button>
            {hasAvatar && (
              <button type="button" onClick={onRemoveAvatar} disabled={deleting}>
                {deleting ? "Kaldırılıyor…" : "Kaldır"}
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
            JPEG/PNG/WebP, maksimum 5 MB. Resmi seçtikten sonra kırpma ekranı açılacak.
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
        </div>

        {/* Büyük görüntüleme */}
        <ImageModal
          open={openAvatar}
          src={avatar.big ?? undefined}
          alt={user.username}
          caption={`@${user.username}`}
          onClose={() => setOpenAvatar(false)}
        />
      </section>

      {/* Profil formu */}
      <form onSubmit={onSave}>
        {/* Dil seçimi */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
            Tercih Edilen Dil
          </label>
          <select
            value={form.preferredLanguage}
            onChange={(e) => setForm((f) => ({ ...f, preferredLanguage: e.target.value }))}
          >
            {LANGS.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
          <small className="muted" style={{ display: "block", marginTop: 6 }}>
            Çeviri butonu ve arayüz metinleri bu dile göre gösterilir.
          </small>
        </div>

        <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Biyografi</label>
        <textarea
          rows={4}
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 500) })}
          style={{ width: "100%", marginBottom: 12, padding: 8 }}
          placeholder="Kendinden kısaca bahset..."
          maxLength={500}
        />

        <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Favori Söz</label>
        <input
          value={form.favoriteQuote}
          onChange={(e) => setForm({ ...form, favoriteQuote: e.target.value.slice(0, 300) })}
          style={{ width: "100%", marginBottom: 12, padding: 8 }}
          placeholder="Örn: 'Stay hungry, stay foolish.'"
          maxLength={300}
        />

        <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Website</label>
        <input
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value.slice(0, 200) })}
          style={{ width: "100%", marginBottom: 16, padding: 8 }}
          placeholder="https://..."
          maxLength={200}
        />

        <button disabled={saving} type="submit" style={{ padding: "10px 12px" }}>
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </form>

      {msg && <p style={{ color: "seagreen", marginTop: 12 }}>{msg}</p>}
      {err && <p style={{ color: "crimson", marginTop: 12 }}>{err}</p>}

      {/* ========== Gizlilik ve Güvenlik ========== */}
      <section
        style={{
          border: "1px solid #2c2c2c",
          borderRadius: 8,
          padding: 16,
          marginTop: 24,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 16 }}>Gizlilik ve Güvenlik</div>

        {/* Kullanıcı Adı Değiştir - Collapsible */}
        <div style={{ marginBottom: 12, borderBottom: "1px solid #2c2c2c", paddingBottom: 12 }}>
          <button
            onClick={() => setUsernameOpen(!usernameOpen)}
            style={{
              width: "100%",
              padding: "12px",
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600
            }}
          >
            <span>Kullanıcı Adı</span>
            <span style={{ transform: usernameOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▼</span>
          </button>
          
          {usernameOpen && (
            <div style={{ marginTop: 12, paddingLeft: 12 }}>
              <form onSubmit={onChangeUsername} style={{ display: "flex", gap: 8 }}>
                <input
                  value={uname}
                  onChange={(e) => setUname(e.target.value.slice(0, 24))}
                  placeholder="@kullanici"
                  style={{ flex: 1, padding: 8 }}
                  maxLength={24}
                />
                <button type="submit" disabled={unameBusy}>
                  {unameBusy ? "Güncelleniyor…" : "Güncelle"}
                </button>
              </form>
              {unameMsg && <small style={{ color: "seagreen", display: "block", marginTop: 6 }}>{unameMsg}</small>}
              {unameErr && <small style={{ color: "crimson", display: "block", marginTop: 6 }}>{unameErr}</small>}
            </div>
          )}
        </div>

        {/* Şifre Değiştir - Collapsible */}
        <div style={{ marginBottom: 12, borderBottom: "1px solid #2c2c2c", paddingBottom: 12 }}>
          <button
            onClick={() => setPasswordOpen(!passwordOpen)}
            style={{
              width: "100%",
              padding: "12px",
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600
            }}
          >
            <span>Şifre Değiştir</span>
            <span style={{ transform: passwordOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▼</span>
          </button>

          {passwordOpen && (
            <div style={{ marginTop: 12, paddingLeft: 12 }}>
              <form onSubmit={onChangePassword}>
                <input
                  type="password"
                  placeholder="Mevcut şifre"
                  value={pwdCur}
                  onChange={(e) => setPwdCur(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 8 }}
                  autoComplete="current-password"
                />
                <input
                  type="password"
                  placeholder="Yeni şifre (min 6)"
                  value={pwdNew}
                  onChange={(e) => setPwdNew(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 8 }}
                  autoComplete="new-password"
                />
                <input
                  type="password"
                  placeholder="Yeni şifre (tekrar)"
                  value={pwdNew2}
                  onChange={(e) => setPwdNew2(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 8 }}
                  autoComplete="new-password"
                />
                <button type="submit" disabled={pwdBusy}>
                  {pwdBusy ? "Güncelleniyor…" : "Şifreyi Güncelle"}
                </button>
              </form>
              {pwdMsg && <small style={{ color: "seagreen", display: "block", marginTop: 6 }}>{pwdMsg}</small>}
              {pwdErr && <small style={{ color: "crimson", display: "block", marginTop: 6 }}>{pwdErr}</small>}
            </div>
          )}
        </div>

        {/* Hesabı Kapat - Collapsible */}
        <div>
          <button
            onClick={() => setDeleteOpen(!deleteOpen)}
            style={{
              width: "100%",
              padding: "12px",
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600
            }}
          >
            <span>Hesabı Kapat</span>
            <span style={{ transform: deleteOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▼</span>
          </button>

          {deleteOpen && (
            <div style={{ marginTop: 12, paddingLeft: 12 }}>
              <button 
                type="button" 
                onClick={onSoftDelete} 
                style={{ 
                  background: "#3a0a0a", 
                  border: "1px solid #5a1a1a", 
                  padding: "8px 12px", 
                  borderRadius: 6,
                  color: "#ff6b6b",
                  cursor: "pointer"
                }}
              >
                Hesabı Kapat (Soft Delete)
              </button>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                Bu işlem hesabını pasifleştirir (soft delete). İleride aynı kullanıcı adını yeniden alabilirsin.
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
